import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, oneOf, optional, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { readResponse } from 'api/shared/readResponse';

import {
  TRIGGER_ACTIONS,
  TRIGGER_RESTART_FIRE_MODE_DEFAULT,
  TRIGGER_RESTART_FIRE_MODES,
  triggerParameterSchema,
  type TriggerAction,
  type TriggerParameter,
  type TriggerRestartFireMode,
} from './types';
import type { SaveTrigger } from './postTrigger';

// PUT /triggers/:trigger_id — update. Backend: routes/triggers.js updates the
// trigger and its status-change row, then re-reads `SELECT t.*, z.name AS zone_name`
// and attaches `folder_path`, the `status_change` sub-record and the monitor zones.
//
// `UpdatedTrigger` and its nested `UpdatedTriggerStatusChange` are their OWN types +
// schemas for the same reason as the Created pair: a distinct backend statement — the
// third and fourth verbatim copies of the same two SELECTs — whose shape happens to
// coincide (see ../../README.md). Structural typing lets the result flow wherever a
// `Trigger` is expected, which the edit model's reinitialize relies on.

export interface UpdatedTriggerStatusChange {
  readonly trigger_node_status_change_id: number;
  readonly trigger_id: number;
  readonly node_definition_id: number | null;
  readonly node_definition_name: string | null;
  readonly from_status: string | null;
  readonly to_status: string | null;
  readonly action: TriggerAction;
  readonly target_node_definition_id: number | null;
  readonly target_node_definition_name: string | null;
  readonly target_default_queue_id: number | null;
  readonly target_event_id: number | null;
  readonly target_event_name: string | null;
  readonly target_event_zone_name: string | null;
  readonly queue_id: number | null;
  readonly queue_name: string | null;
  readonly queue_zone_name: string | null;
  readonly parameters: readonly TriggerParameter[] | null;
  readonly restart_fire_mode: TriggerRestartFireMode;
  readonly timezone: string | null;
  // Nullable for the same reason as on the reads: the columns carry a default but
  // no NOT NULL. See getTriggers.
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface UpdatedTrigger {
  readonly trigger_id: number;
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  readonly name: string;
  readonly type: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly monitor_all_zones: boolean;
  readonly monitor_zone_ids: readonly number[];
  readonly monitor_zone_names: readonly string[];
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly status_change?: UpdatedTriggerStatusChange;
}

const updatedStatusChangeSchema = object<UpdatedTriggerStatusChange>({
  trigger_node_status_change_id: num(),
  trigger_id: num(),
  node_definition_id: nullable(num()),
  node_definition_name: nullable(str()),
  from_status: nullable(str()),
  to_status: nullable(str()),
  action: oneOf(TRIGGER_ACTIONS, 'raise_event'),
  target_node_definition_id: nullable(num()),
  target_node_definition_name: nullable(str()),
  target_default_queue_id: nullable(num()),
  target_event_id: nullable(num()),
  target_event_name: nullable(str()),
  target_event_zone_name: nullable(str()),
  queue_id: nullable(num()),
  queue_name: nullable(str()),
  queue_zone_name: nullable(str()),
  parameters: nullable(array(triggerParameterSchema)),
  restart_fire_mode: oneOf(TRIGGER_RESTART_FIRE_MODES, TRIGGER_RESTART_FIRE_MODE_DEFAULT),
  timezone: nullable(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

const updatedSchema = object<UpdatedTrigger>({
  trigger_id: num(REJECT),
  zone_id: nullable(num()),
  zone_name: nullable(str()),
  name: str(),
  type: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  monitor_all_zones: bool(),
  monitor_zone_ids: array(num()),
  monitor_zone_names: array(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  status_change: optional(updatedStatusChangeSchema),
});

/**
 * PUT `/triggers/:trigger_id`. The trigger is addressed by its numeric id (unlike
 * locks, which are addressed by their pre-edit zone/name path — a trigger's id is
 * stable across a rename, so nothing has to be carried from before the edit).
 *
 * Returns the updated record.
 *
 * Rejects on HTTP error — nothing was written, and the server's complaint (400 /
 * 403 / 404 / 409) is the useful thing to report.
 *
 * Answers with the **degraded** or **unreadable** arm when the update landed and
 * its receipt cannot be read in full. The write is committed by then, so reporting
 * it as a failed save would invite a retry of an edit that already applied (#1035).
 *
 * Only `trigger_id` carries `REJECT`, for the reason spelled out on `postTrigger`: no
 * caller opens a form on this value.
 */
export const putTrigger = async (id: number, save: SaveTrigger): Promise<Received<UpdatedTrigger>> => {
  const res = await api.put(`/triggers/${id}`, save);
  return readResponse(res.data, { schema: updatedSchema, source: 'putTrigger' });
};
